import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Settings, Save, Plus, Edit2, Key, FileText, Cog, Trash2, CheckCircle, XCircle, Loader2, RefreshCw, Users, UserPlus, BarChart3 } from 'lucide-react';
import { useState, useEffect, useCallback } from 'react';
import { adminApi, type AdminModel, type AdminPrompt, type CreateModelRequest, type CreatePromptRequest, type CreateSettingRequest } from '../api/admin';
import { usersApi, type User, type CreateUserRequest, type UpdateUserRequest } from '../api/users';
import { useAuth } from '../contexts/useAuth';
import Card from '../components/Card';
import Button from '../components/Button';
import Input from '../components/Input';
import Textarea from '../components/Textarea';
import Modal from '../components/Modal';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';
import AnalyticsTab from '../components/AnalyticsTab';
import UserUsageTab from '../components/UserUsageTab';

type HttpError = {
  message?: string;
  response?: {
    status?: number;
    data?: unknown;
    config?: { url?: string };
  };
};

const getHttpError = (error: unknown): HttpError => (error ?? {}) as HttpError;

const getHttpErrorMessage = (error: unknown): string => {
  const err = getHttpError(error);
  const data = err.response?.data;
  if (data && typeof data === 'object') {
    const obj = data as { message?: unknown; error?: unknown };
    if (typeof obj.message === 'string' && obj.message.trim()) return obj.message;
    if (typeof obj.error === 'string' && obj.error.trim()) return obj.error;
  }
  if (typeof data === 'string' && data.trim()) return data;
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  return 'Неизвестная ошибка';
};

// Approximate pricing per 1M tokens and per chapter (50 pages, ~500 tokens/page)
function getModelPricing(modelName: string): { input: number; output: number; chapterCost: number } {
  const m = modelName.toLowerCase();
  // Average tokens per chapter: ~25K input, ~5K output for OCR; ~10K input, ~8K output for translation
  const avgInputTokens = 25_000;
  const avgOutputTokens = 8_000;

  let input = 2.50, output = 10.00;

  if (m.includes('gpt-4o') && !m.includes('mini')) { input = 2.50; output = 10.00; }
  else if (m.includes('gpt-4o-mini')) { input = 0.15; output = 0.60; }
  else if (m.includes('gpt-5') && m.includes('mini')) { input = 0.25; output = 2.00; }
  else if (m.includes('gpt-5')) { input = 1.25; output = 5.00; }
  else if (m.includes('gemini-3.1-pro')) { input = 2.00; output = 12.00; }
  else if (m.includes('gemini-3') && m.includes('flash')) { input = 0.50; output = 3.00; }
  else if (m.includes('gemini-3') && m.includes('pro')) { input = 2.00; output = 12.00; }
  else if (m.includes('gemini-2.5-flash') && m.includes('lite')) { input = 0.10; output = 0.40; }
  else if (m.includes('gemini-2.5-flash')) { input = 0.30; output = 2.50; }
  else if (m.includes('gemini-2.5-pro')) { input = 1.25; output = 10.00; }
  else if (m.includes('gemini-2.0-flash')) { input = 0.075; output = 0.30; }
  else if (m.includes('gemini-1.5-flash')) { input = 0.075; output = 0.30; }
  else if (m.includes('gemini-1.5-pro')) { input = 1.25; output = 5.00; }
  else if (m.includes('claude') && m.includes('haiku')) { input = 0.25; output = 1.25; }
  else if (m.includes('claude') && m.includes('sonnet')) { input = 3.00; output = 15.00; }
  else if (m.includes('claude') && m.includes('opus')) { input = 15.00; output = 75.00; }

  const chapterCost = (avgInputTokens / 1_000_000) * input + (avgOutputTokens / 1_000_000) * output;
  return { input, output, chapterCost };
}

export default function AdminPage() {
  const queryClient = useQueryClient();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'models' | 'prompts' | 'settings' | 'users' | 'analytics' | 'usage'>('models');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [authApiKey, setAuthApiKey] = useState('');
  const [isAuthRequired, setIsAuthRequired] = useState(false);
  const [isModelModalOpen, setIsModelModalOpen] = useState(false);
  const [isPromptModalOpen, setIsPromptModalOpen] = useState(false);
  const [isApiKeyModalOpen, setIsApiKeyModalOpen] = useState(false);
  const [isProviderApiKeyModalOpen, setIsProviderApiKeyModalOpen] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<'openai' | 'gemini' | 'anthropic' | null>(null);
  const [providerApiKey, setProviderApiKey] = useState('');
  const [editingModel, setEditingModel] = useState<AdminModel | null>(null);
  const [editingPrompt, setEditingPrompt] = useState<AdminPrompt | null>(null);
  const [selectedModelName, setSelectedModelName] = useState<string>('');

  // API key verification state
  const [apiKeyStatus, setApiKeyStatus] = useState<{
    openai: { valid: boolean | null; error: string | null; checking: boolean };
    gemini: { valid: boolean | null; error: string | null; checking: boolean };
    anthropic: { valid: boolean | null; error: string | null; checking: boolean };
  }>({
    openai: { valid: null, error: null, checking: false },
    gemini: { valid: null, error: null, checking: false },
    anthropic: { valid: null, error: null, checking: false },
  });

  // Model form state
  const [modelName, setModelName] = useState('');
  const [provider, setProvider] = useState('openai');
  const [modelModelName, setModelModelName] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [deprecated, setDeprecated] = useState(false);
  const [apiKey, setApiKey] = useState('');

  // Settings state
  const [activeOcrModel, setActiveOcrModel] = useState('');
  const [activeTranslationModel, setActiveTranslationModel] = useState('');
  const [activeOcrPrompt, setActiveOcrPrompt] = useState('');
  const [activeTranslationPrompt, setActiveTranslationPrompt] = useState('');
  const [activeGlossaryPrompt, setActiveGlossaryPrompt] = useState('');

  // Prompt form state
  const [promptName, setPromptName] = useState('');
  const [taskType, setTaskType] = useState('ocr');
  const [promptContent, setPromptContent] = useState('');
  const [promptIsActive, setPromptIsActive] = useState(true);

  // Users state
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');
  const [userUsername, setUserUsername] = useState('');
  const [userRole, setUserRole] = useState<'admin' | 'user'>('user');

  // If no API key is configured via env, require it from the user (stored in localStorage).
  // BUT: Skip this requirement if user is admin (admins use JWT authentication)
  useEffect(() => {
    // If user is admin, they don't need API key (they use JWT)
    if (isAdmin) {
      setIsAuthRequired(false);
      setIsAuthModalOpen(false);
      return;
    }
    
    const hasEnvKey = !!import.meta.env.VITE_API_KEY;
    const hasStoredKey = typeof window !== 'undefined' && !!window.localStorage.getItem('apiKey');
    const required = !hasEnvKey && !hasStoredKey;
    setIsAuthRequired(required);
    if (required) setIsAuthModalOpen(true);
  }, [isAdmin]);

  const { data: models, isLoading: modelsLoading, error: modelsError } = useQuery({
    queryKey: ['admin', 'models'],
    queryFn: () => adminApi.models.getAll(),
    retry: 2,
  });

  const { data: prompts, isLoading: promptsLoading, error: promptsError } = useQuery({
    queryKey: ['admin', 'prompts'],
    queryFn: () => adminApi.prompts.getAll(),
    retry: 2,
  });

  const { data: settings, isLoading: settingsLoading } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => adminApi.settings.getAll(),
    retry: 2,
  });

  const { data: ocrCacheStats, refetch: refetchOcrCacheStats } = useQuery({
    queryKey: ['admin', 'cache', 'ocr', 'stats'],
    queryFn: () => adminApi.cache.getOcrStats(),
    retry: 2,
  });

  const { data: users, isLoading: usersLoading, error: usersError } = useQuery({
    queryKey: ['admin', 'users'],
    queryFn: () => usersApi.getAll(),
    retry: 2,
  });

  const clearOcrCacheMutation = useMutation({
    mutationFn: () => adminApi.cache.clearOcr(),
    onSuccess: () => {
      refetchOcrCacheStats();
      alert('Кэш OCR успешно очищен');
    },
    onError: (error: unknown) => {
      alert(`Ошибка при очистке кэша: ${getHttpErrorMessage(error)}`);
    },
  });

  const modelMutation = useMutation({
    mutationFn: (data: CreateModelRequest) => adminApi.models.createOrUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
      resetModelForm();
    },
    onError: (error: unknown) => {
      console.error('[AdminPage] Model mutation error:', error);
      const err = getHttpError(error);
      if (err.response?.data) {
        console.error('[AdminPage] Server error details:', JSON.stringify(err.response.data, null, 2));
        alert(`Ошибка при сохранении модели: ${getHttpErrorMessage(error)}\n\nДетали: ${JSON.stringify(err.response.data, null, 2)}`);
      } else {
        alert(`Ошибка при сохранении модели: ${getHttpErrorMessage(error)}`);
      }
    },
  });

  const apiKeyMutation = useMutation({
    mutationFn: ({ name, key }: { name: string; key: string }) => adminApi.models.updateApiKey(name, key),
    onSuccess: () => {
      setIsApiKeyModalOpen(false);
      setApiKey('');
      setSelectedModelName('');
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
    },
  });

  const deleteModelMutation = useMutation({
    mutationFn: (name: string) => adminApi.models.delete(name),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'models'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
    onError: (error: unknown) => {
      console.error('[AdminPage] Delete model error:', error);
      const err = getHttpError(error);
      const data = err.response?.data;
      if (data && typeof data === 'object') {
        const errorData = data as { usedInSettings?: unknown; message?: unknown };
        if (errorData.usedInSettings === true) {
          const lastPathPart = err.response?.config?.url?.split('/').pop() || 'модель';
          const msg = typeof errorData.message === 'string' ? errorData.message : 'Модель используется в настройках';
          alert(`Нельзя удалить модель "${lastPathPart}": ${msg}`);
        } else {
          alert(`Ошибка при удалении модели: ${getHttpErrorMessage(error)}`);
        }
      } else {
        alert(`Ошибка при удалении модели: ${getHttpErrorMessage(error)}`);
      }
    },
  });

  const providerApiKeyMutation = useMutation({
    mutationFn: ({ provider, key }: { provider: 'openai' | 'gemini' | 'anthropic'; key: string }) =>
      adminApi.providers.updateApiKey(provider, key),
    onSuccess: (_, variables) => {
      const provider = variables.provider;
      setIsProviderApiKeyModalOpen(false);
      setProviderApiKey('');
      setSelectedProvider(null);
      // Verify the key after updating
      verifyApiKey(provider);
    },
  });

  const verifyApiKey = useCallback(async (provider: 'openai' | 'gemini' | 'anthropic') => {
    setApiKeyStatus(prev => ({
      ...prev,
      [provider]: { ...prev[provider], checking: true, error: null }
    }));

    try {
      const result = await adminApi.providers.verifyApiKey(provider);
      setApiKeyStatus(prev => ({
        ...prev,
        [provider]: { valid: result.valid, error: result.error || null, checking: false }
      }));
    } catch (error: unknown) {
      setApiKeyStatus(prev => ({
        ...prev,
        [provider]: { 
          valid: false, 
          error: getHttpErrorMessage(error) || 'Ошибка при проверке ключа', 
          checking: false 
        }
      }));
    }
  }, []);

  // Auto-verify keys when settings tab is opened
  useEffect(() => {
    if (activeTab === 'settings') {
      verifyApiKey('openai');
      verifyApiKey('gemini');
      verifyApiKey('anthropic');
    }
  }, [activeTab, verifyApiKey]);

  const promptMutation = useMutation({
    mutationFn: (data: CreatePromptRequest) => adminApi.prompts.createOrUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'prompts'] });
      resetPromptForm();
    },
  });

  const settingMutation = useMutation({
    mutationFn: (data: CreateSettingRequest) => adminApi.settings.createOrUpdate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  const userMutation = useMutation({
    mutationFn: (data: CreateUserRequest | UpdateUserRequest) => 
      editingUser 
        ? usersApi.update(editingUser.id, data as UpdateUserRequest)
        : usersApi.create(data as CreateUserRequest),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
      resetUserForm();
    },
    onError: (error: unknown) => {
      console.error('[AdminPage] User mutation error:', error);
      alert(`Ошибка: ${getHttpErrorMessage(error)}`);
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (id: string) => usersApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'users'] });
    },
    onError: (error: unknown) => {
      console.error('[AdminPage] Delete user error:', error);
      alert(`Ошибка при удалении пользователя: ${getHttpErrorMessage(error)}`);
    },
  });

  const resetModelForm = () => {
    setIsModelModalOpen(false);
    setEditingModel(null);
    setModelName('');
    setProvider('openai');
    setModelModelName('');
    setBaseUrl('');
    setIsActive(false);
    setDeprecated(false);
  };

  const resetPromptForm = () => {
    setIsPromptModalOpen(false);
    setEditingPrompt(null);
    setPromptName('');
    setTaskType('ocr');
    setPromptContent('');
    setPromptIsActive(true);
  };

  const resetUserForm = () => {
    setIsUserModalOpen(false);
    setEditingUser(null);
    setUserEmail('');
    setUserPassword('');
    setUserUsername('');
    setUserRole('user');
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setUserEmail(user.email);
    setUserUsername(user.username || '');
    setUserRole(user.role);
    setUserPassword(''); // Don't show password
    setIsUserModalOpen(true);
  };

  const handleUserSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingUser) {
      // Update existing user
      const updateData: UpdateUserRequest = {
        email: userEmail,
        username: userUsername || undefined,
        role: userRole,
      };
      
      // Only include password if provided
      if (userPassword.trim()) {
        updateData.password = userPassword;
      }
      
      userMutation.mutate(updateData);
    } else {
      // Create new user
      if (!userPassword.trim()) {
        alert('Пароль обязателен при создании нового пользователя');
        return;
      }
      
      const createData: CreateUserRequest = {
        email: userEmail,
        password: userPassword,
        username: userUsername || undefined,
        role: userRole,
      };
      
      userMutation.mutate(createData);
    }
  };

  const handleEditModel = (model: AdminModel) => {
    setEditingModel(model);
    setModelName(model.name);
    setProvider(model.provider);
    setModelModelName(model.model_name);
    setBaseUrl(model.base_url || '');
    setIsActive(model.is_active);
    setDeprecated(model.deprecated ?? false);
    setIsModelModalOpen(true);
  };

  const handleEditPrompt = (prompt: AdminPrompt) => {
    setEditingPrompt(prompt);
    setPromptName(prompt.name);
    setTaskType(prompt.task_type);
    setPromptContent(prompt.content);
    setPromptIsActive(prompt.is_active);
    setIsPromptModalOpen(true);
  };

  const handleModelSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Устанавливаем base_url по умолчанию для разных провайдеров
    let defaultBaseUrl = baseUrl && typeof baseUrl === 'string' ? baseUrl.trim() : '';
    if (!defaultBaseUrl) {
      if (provider === 'gemini') {
        defaultBaseUrl = 'https://generativelanguage.googleapis.com/v1beta';
      } else if (provider === 'openai') {
        defaultBaseUrl = 'https://api.openai.com/v1';
      } else if (provider === 'anthropic') {
        defaultBaseUrl = 'https://api.anthropic.com/v1';
      }
    }
    
    const modelData: CreateModelRequest = {
      name: modelName.trim(),
      provider: provider.trim(),
      model_name: modelModelName.trim(),
      base_url: defaultBaseUrl || undefined,
      // Автоматически устанавливаем эмбеддинги по умолчанию
      embeddings_model: 'text-embedding-3-small',
      embeddings_base_url: 'https://api.openai.com/v1',
      is_active: isActive,
      deprecated: deprecated,
      // API ключи заполняются через отдельное окно
    };
    
    console.log('[AdminPage] Submitting model data:', { ...modelData, api_key: '[REDACTED]' });
    modelMutation.mutate(modelData);
  };

  const handlePromptSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    promptMutation.mutate({
      name: promptName,
      task_type: taskType,
      content: promptContent,
      is_active: promptIsActive,
    });
  };

  const handleUpdateApiKey = (name: string) => {
    setSelectedModelName(name);
    setIsApiKeyModalOpen(true);
  };

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!apiKey.trim()) return;
    apiKeyMutation.mutate({ name: selectedModelName, key: apiKey });
  };

  // Load settings into state when they're available
  useEffect(() => {
    if (settings) {
      const ocrModel = settings.find(s => s.key === 'active_ocr_model');
      const translationModel = settings.find(s => s.key === 'active_translation_model');
      const ocrPrompt = settings.find(s => s.key === 'active_ocr_prompt');
      const translationPrompt = settings.find(s => s.key === 'active_translation_prompt');
      const glossaryPrompt = settings.find(s => s.key === 'active_glossary_prompt');
      
      if (ocrModel) setActiveOcrModel(ocrModel.value || '');
      if (translationModel) setActiveTranslationModel(translationModel.value || '');
      if (ocrPrompt) setActiveOcrPrompt(ocrPrompt.value || '');
      if (translationPrompt) setActiveTranslationPrompt(translationPrompt.value || '');
      if (glossaryPrompt) setActiveGlossaryPrompt(glossaryPrompt.value || '');
    }
  }, [settings]);

  const handleSaveSetting = (key: string, value: string, description: string) => {
    settingMutation.mutate({ key, value, description });
  };

  if (modelsLoading || promptsLoading || settingsLoading || usersLoading) {
    return (
      <div>
        {/* API Key Gate */}
        <Modal
          isOpen={isAuthModalOpen && !isAdmin}
          onClose={() => {}}
          title="Доступ к API"
          footer={
            <Button
              onClick={() => {
                if (!authApiKey.trim()) return;
                setApiKey(authApiKey);
                setIsAuthModalOpen(false);
                setAuthApiKey('');
                // Refetch after setting key
                queryClient.invalidateQueries({ queryKey: ['admin'] });
              }}
              disabled={!authApiKey.trim()}
            >
              <Key className="h-4 w-4 mr-2" />
              Продолжить
            </Button>
          }
        >
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Для доступа к админ-панели нужен{' '}
              <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">X-API-Key</code>.
              Он будет сохранён в браузере (localStorage) только для локальной разработки.
            </p>
            <Input
              label="API ключ"
              type="password"
              value={authApiKey}
              onChange={(e) => setAuthApiKey(e.target.value)}
              required
              placeholder="Введите BACKEND_API_KEY"
            />
          </div>
        </Modal>

        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div>
      {/* API Key Gate */}
      <Modal
        isOpen={isAuthModalOpen && !isAdmin}
        onClose={() => {
          if (isAuthRequired) return;
          setIsAuthModalOpen(false);
          setAuthApiKey('');
        }}
        title="Доступ к API"
        footer={
          <Button
            onClick={() => {
              if (!authApiKey.trim()) return;
              setApiKey(authApiKey);
              setIsAuthModalOpen(false);
              setAuthApiKey('');
              // Refetch after setting key
              queryClient.invalidateQueries({ queryKey: ['admin'] });
            }}
            disabled={!authApiKey.trim()}
          >
            <Key className="h-4 w-4 mr-2" />
            Продолжить
          </Button>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Для доступа к админ-панели нужен <code className="px-1 py-0.5 bg-gray-100 dark:bg-gray-700 rounded">X-API-Key</code>.
            Он будет сохранён в браузере (localStorage) только для локальной разработки.
          </p>
          <Input
            label="API ключ"
            type="password"
            value={authApiKey}
            onChange={(e) => setAuthApiKey(e.target.value)}
            required
            placeholder="Введите BACKEND_API_KEY"
          />
        </div>
      </Modal>

      <div className="mb-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Settings className="h-8 w-8" />
              Админ панель
            </h1>
            <p className="text-gray-500 dark:text-gray-400 mt-2">
              Настройка моделей, API ключей и промптов
            </p>
          </div>
          {!isAdmin && (
            <Button variant="secondary" onClick={() => setIsAuthModalOpen(true)}>
              <Key className="h-4 w-4 mr-2" />
              API ключ
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6 border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-6 overflow-x-auto whitespace-nowrap">
          <button
            onClick={() => setActiveTab('models')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'models'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Модели
          </button>
          <button
            onClick={() => setActiveTab('prompts')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'prompts'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            Промпты
          </button>
          <button
            onClick={() => setActiveTab('settings')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'settings'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Cog className="h-4 w-4 inline mr-1" />
            Настройки
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-1" />
            Пользователи
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'analytics'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <BarChart3 className="h-4 w-4 inline mr-1" />
            Аналитика
          </button>
          <button
            onClick={() => setActiveTab('usage')}
            className={`py-4 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'usage'
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
            }`}
          >
            <Users className="h-4 w-4 inline mr-1" />
            Учёт
          </button>
        </nav>
      </div>

      {/* Models Tab */}
      {activeTab === 'models' && (
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Модели</h2>
            <Button className="w-full sm:w-auto flex items-center justify-center" onClick={() => setIsModelModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить модель
            </Button>
          </div>

          {modelsError ? (
            <ErrorMessage message={`Ошибка загрузки моделей: ${modelsError instanceof Error ? modelsError.message : 'Неизвестная ошибка'}`} />
          ) : (
            <Card>
              {models && models.length > 0 ? (
                <>
                  <div className="sm:hidden space-y-3">
                    {models.map((model) => (
                      <div
                        key={model.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                              {model.name}
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-words">
                              {model.provider} • {model.model_name}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEditModel(model)}
                              className="p-2 rounded-md text-primary-600 hover:bg-primary-50 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                              title="Редактировать"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleUpdateApiKey(model.name)}
                              className="p-2 rounded-md text-primary-600 hover:bg-primary-50 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                              title="Обновить API ключ"
                            >
                              <Key className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Вы уверены, что хотите удалить модель "${model.name}"? Это действие нельзя отменить.`)) {
                                  deleteModelMutation.mutate(model.name);
                                }
                              }}
                              className="p-2 rounded-md text-red-600 hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 disabled:opacity-50"
                              title="Удалить модель"
                              disabled={deleteModelMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              model.is_active
                                ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {model.is_active ? 'Активна' : 'Неактивна'}
                          </span>
                          {model.deprecated && (
                            <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                              Устарела
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Название
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Провайдер
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Модель
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Статус
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {models.map((model) => (
                          <tr key={model.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${model.deprecated ? 'opacity-60' : ''}`}>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {model.name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {model.provider}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {model.model_name}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex flex-wrap items-center gap-2">
                                <span
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    model.is_active
                                      ? 'bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100'
                                      : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                  }`}
                                >
                                  {model.is_active ? 'Активна' : 'Неактивна'}
                                </span>
                                {model.deprecated && (
                                  <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                    Устарела
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditModel(model)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  title="Редактировать"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleUpdateApiKey(model.name)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  title="Обновить API ключ"
                                >
                                  <Key className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Вы уверены, что хотите удалить модель "${model.name}"? Это действие нельзя отменить.`)) {
                                      deleteModelMutation.mutate(model.name);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  title="Удалить модель"
                                  disabled={deleteModelMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Модели не настроены
                </p>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Prompts Tab */}
      {activeTab === 'prompts' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Промпты</h2>
            <Button onClick={() => setIsPromptModalOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Добавить промпт
            </Button>
          </div>

          {promptsError ? (
            <ErrorMessage message={`Ошибка загрузки промптов: ${promptsError instanceof Error ? promptsError.message : 'Неизвестная ошибка'}`} />
          ) : (
            <div className="space-y-4">
              {prompts && prompts.length > 0 ? (
                prompts.map((prompt) => (
                  <Card key={prompt.id}>
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <FileText className="h-5 w-5 text-gray-400" />
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {prompt.name}
                          </h3>
                          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            {prompt.task_type}
                          </span>
                          <span className="text-xs px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded">
                            v{prompt.version}
                          </span>
                          {prompt.is_active && (
                            <span className="text-xs px-2 py-1 bg-green-100 dark:bg-green-800 text-green-800 dark:text-green-100 rounded">
                              Активен
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3 line-clamp-3">
                          {prompt.content.substring(0, 200)}...
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                          Обновлено: {new Date(prompt.updated_at).toLocaleString('ru-RU')}
                        </p>
                      </div>
                      <button
                        onClick={() => handleEditPrompt(prompt)}
                        className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300 ml-4"
                        title="Редактировать"
                      >
                        <Edit2 className="h-5 w-5" />
                      </button>
                    </div>
                  </Card>
                ))
              ) : (
                <Card>
                  <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                    Промпты не настроены
                  </p>
                </Card>
              )}
            </div>
          )}
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <div>
          <div className="mb-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Настройки системы</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Выберите активные модели и промпты для работы сайта и воркеров
            </p>
          </div>

          <div className="space-y-6">
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">API ключи провайдеров</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Глобальные API ключи для всех моделей провайдера. Эти ключи будут использоваться, если у модели не указан собственный ключ.
              </p>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-medium text-gray-900 dark:text-white">OpenAI</div>
                      {apiKeyStatus.openai.checking ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : apiKeyStatus.openai.valid === true ? (
                        <span title="Ключ работает"><CheckCircle className="h-4 w-4 text-green-500" /></span>
                      ) : apiKeyStatus.openai.valid === false ? (
                        <span title={apiKeyStatus.openai.error || 'Ключ не работает'}><XCircle className="h-4 w-4 text-red-500" /></span>
                      ) : null}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Используется для всех моделей OpenAI</div>
                    {apiKeyStatus.openai.error && apiKeyStatus.openai.valid === false && (
                      <div className="text-xs text-red-500 dark:text-red-400 mt-1">{apiKeyStatus.openai.error}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => verifyApiKey('openai')}
                      disabled={apiKeyStatus.openai.checking}
                      title="Проверить ключ"
                    >
                      <RefreshCw className={`h-4 w-4 ${apiKeyStatus.openai.checking ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSelectedProvider('openai');
                        setIsProviderApiKeyModalOpen(true);
                      }}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Настроить ключ
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-medium text-gray-900 dark:text-white">Google Gemini</div>
                      {apiKeyStatus.gemini.checking ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : apiKeyStatus.gemini.valid === true ? (
                        <span title="Ключ работает"><CheckCircle className="h-4 w-4 text-green-500" /></span>
                      ) : apiKeyStatus.gemini.valid === false ? (
                        <span title={apiKeyStatus.gemini.error || 'Ключ не работает'}><XCircle className="h-4 w-4 text-red-500" /></span>
                      ) : null}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Используется для всех моделей Gemini</div>
                    {apiKeyStatus.gemini.error && apiKeyStatus.gemini.valid === false && (
                      <div className="text-xs text-red-500 dark:text-red-400 mt-1">{apiKeyStatus.gemini.error}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => verifyApiKey('gemini')}
                      disabled={apiKeyStatus.gemini.checking}
                      title="Проверить ключ"
                    >
                      <RefreshCw className={`h-4 w-4 ${apiKeyStatus.gemini.checking ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSelectedProvider('gemini');
                        setIsProviderApiKeyModalOpen(true);
                      }}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Настроить ключ
                    </Button>
                  </div>
                </div>
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="font-medium text-gray-900 dark:text-white">Anthropic (Claude)</div>
                      {apiKeyStatus.anthropic.checking ? (
                        <Loader2 className="h-4 w-4 text-gray-400 animate-spin" />
                      ) : apiKeyStatus.anthropic.valid === true ? (
                        <span title="Ключ работает"><CheckCircle className="h-4 w-4 text-green-500" /></span>
                      ) : apiKeyStatus.anthropic.valid === false ? (
                        <span title={apiKeyStatus.anthropic.error || 'Ключ не работает'}><XCircle className="h-4 w-4 text-red-500" /></span>
                      ) : null}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Используется для всех моделей Claude</div>
                    {apiKeyStatus.anthropic.error && apiKeyStatus.anthropic.valid === false && (
                      <div className="text-xs text-red-500 dark:text-red-400 mt-1">{apiKeyStatus.anthropic.error}</div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => verifyApiKey('anthropic')}
                      disabled={apiKeyStatus.anthropic.checking}
                      title="Проверить ключ"
                    >
                      <RefreshCw className={`h-4 w-4 ${apiKeyStatus.anthropic.checking ? 'animate-spin' : ''}`} />
                    </Button>
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setSelectedProvider('anthropic');
                        setIsProviderApiKeyModalOpen(true);
                      }}
                    >
                      <Key className="h-4 w-4 mr-2" />
                      Настроить ключ
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Активные модели</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Выберите модель для OCR и перевода. Стоимость указана за 1M токенов (вход/выход).
              </p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Модель для OCR
                  </label>
                  <select
                    value={activeOcrModel}
                    onChange={(e) => {
                      setActiveOcrModel(e.target.value);
                      handleSaveSetting('active_ocr_model', e.target.value, 'Активная модель для OCR');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Не выбрано</option>
                    {models?.filter(m => m.is_active).map((model) => (
                      <option key={model.id} value={model.name}>
                        {model.name} ({model.provider} - {model.model_name}){model.deprecated ? ' — устарела' : ''}
                      </option>
                    ))}
                  </select>
                  {activeOcrModel && (() => {
                    const model = models?.find(m => m.name === activeOcrModel);
                    if (!model) return null;
                    const pricing = getModelPricing(model.model_name);
                    return (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span>Стоимость:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-200">${pricing.input}/1M вх. + ${pricing.output}/1M вых.</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>~Стоимость 1 главы (50 стр.):</span>
                          <span className={`font-medium ${pricing.chapterCost < 1 ? 'text-emerald-600 dark:text-emerald-400' : pricing.chapterCost < 3 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                            ~${pricing.chapterCost.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Модель для перевода
                  </label>
                  <select
                    value={activeTranslationModel}
                    onChange={(e) => {
                      setActiveTranslationModel(e.target.value);
                      handleSaveSetting('active_translation_model', e.target.value, 'Активная модель для перевода');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Не выбрано</option>
                    {models?.filter(m => m.is_active).map((model) => (
                      <option key={model.id} value={model.name}>
                        {model.name} ({model.provider} - {model.model_name}){model.deprecated ? ' — устарела' : ''}
                      </option>
                    ))}
                  </select>
                  {activeTranslationModel && (() => {
                    const model = models?.find(m => m.name === activeTranslationModel);
                    if (!model) return null;
                    const pricing = getModelPricing(model.model_name);
                    return (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span>Стоимость:</span>
                          <span className="font-medium text-gray-700 dark:text-gray-200">${pricing.input}/1M вх. + ${pricing.output}/1M вых.</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span>~Стоимость 1 главы (50 стр.):</span>
                          <span className={`font-medium ${pricing.chapterCost < 1 ? 'text-emerald-600 dark:text-emerald-400' : pricing.chapterCost < 3 ? 'text-amber-600 dark:text-amber-400' : 'text-rose-600 dark:text-rose-400'}`}>
                            ~${pricing.chapterCost.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Активные промпты</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Промпт для OCR
                  </label>
                  <select
                    value={activeOcrPrompt}
                    onChange={(e) => {
                      setActiveOcrPrompt(e.target.value);
                      handleSaveSetting('active_ocr_prompt', e.target.value, 'Активный промпт для OCR');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Не выбрано</option>
                    {prompts?.filter(p => p.task_type === 'ocr' && p.is_active).map((prompt) => (
                      <option key={prompt.id} value={prompt.name}>
                        {prompt.name} (v{prompt.version})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Промпт для перевода
                  </label>
                  <select
                    value={activeTranslationPrompt}
                    onChange={(e) => {
                      setActiveTranslationPrompt(e.target.value);
                      handleSaveSetting('active_translation_prompt', e.target.value, 'Активный промпт для перевода');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Не выбрано</option>
                    {prompts?.filter(p => p.task_type === 'translation' && p.is_active).map((prompt) => (
                      <option key={prompt.id} value={prompt.name}>
                        {prompt.name} (v{prompt.version})
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Промпт для глоссария
                  </label>
                  <select
                    value={activeGlossaryPrompt}
                    onChange={(e) => {
                      setActiveGlossaryPrompt(e.target.value);
                      handleSaveSetting('active_glossary_prompt', e.target.value, 'Активный промпт для глоссария');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="">Не выбрано</option>
                    {prompts?.filter(p => p.task_type === 'glossary-extraction' && p.is_active).map((prompt) => (
                      <option key={prompt.id} value={prompt.name}>
                        {prompt.name} (v{prompt.version})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </Card>

            <Card>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Управление кэшем OCR</h3>
              <div className="space-y-4">
                {ocrCacheStats && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-300">Всего записей в кэше:</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {ocrCacheStats.total_entries || '0'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 dark:text-gray-300">Уникальных хешей:</span>
                        <span className="text-gray-900 dark:text-white font-medium">
                          {ocrCacheStats.unique_hashes || '0'}
                        </span>
                      </div>
                      {ocrCacheStats.oldest_entry && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-300">Самая старая запись:</span>
                          <span className="text-gray-900 dark:text-white font-medium text-xs">
                            {new Date(ocrCacheStats.oldest_entry).toLocaleString('ru-RU')}
                          </span>
                        </div>
                      )}
                      {ocrCacheStats.newest_entry && (
                        <div className="flex items-center justify-between">
                          <span className="text-gray-500 dark:text-gray-300">Самая новая запись:</span>
                          <span className="text-gray-900 dark:text-white font-medium text-xs">
                            {new Date(ocrCacheStats.newest_entry).toLocaleString('ru-RU')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    Очистка кэша OCR удалит все сохраненные результаты распознавания текста. 
                    Это позволит запустить обработку главы с нуля, без использования кэша.
                  </p>
                  <Button
                    variant="danger"
                    onClick={() => {
                      if (window.confirm('Вы уверены, что хотите очистить весь кэш OCR? Это действие нельзя отменить.')) {
                        clearOcrCacheMutation.mutate();
                      }
                    }}
                    disabled={clearOcrCacheMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {clearOcrCacheMutation.isPending ? 'Очистка...' : 'Очистить кэш OCR'}
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Users Tab */}
      {activeTab === 'users' && (
        <div>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Пользователи</h2>
            <Button className="w-full sm:w-auto flex items-center justify-center" onClick={() => setIsUserModalOpen(true)}>
              <UserPlus className="h-4 w-4 mr-2" />
              Добавить пользователя
            </Button>
          </div>

          {usersError ? (
            <ErrorMessage message={`Ошибка загрузки пользователей: ${usersError instanceof Error ? usersError.message : 'Неизвестная ошибка'}`} />
          ) : usersLoading ? (
            <LoadingSpinner />
          ) : (
            <Card>
              {users && users.length > 0 ? (
                <>
                  <div className="sm:hidden space-y-3">
                    {users.map((user) => (
                      <div
                        key={user.id}
                        className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white break-words">
                              {user.email}
                            </div>
                            <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 break-words">
                              {user.username || '—'}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="p-2 rounded-md text-primary-600 hover:bg-primary-50 hover:text-primary-900 dark:text-primary-400 dark:hover:bg-primary-900/20 dark:hover:text-primary-300"
                              title="Редактировать"
                            >
                              <Edit2 className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Вы уверены, что хотите удалить пользователя "${user.email}"? Это действие нельзя отменить.`)) {
                                  deleteUserMutation.mutate(user.id);
                                }
                              }}
                              className="p-2 rounded-md text-red-600 hover:bg-red-50 hover:text-red-900 dark:text-red-400 dark:hover:bg-red-900/20 dark:hover:text-red-300 disabled:opacity-50"
                              title="Удалить пользователя"
                              disabled={deleteUserMutation.isPending}
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          <span
                            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                              user.role === 'admin'
                                ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                                : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                          </span>
                          <span className="text-xs text-gray-600 dark:text-gray-300">
                            Telegram: {user.telegram_id ? user.telegram_id : '—'}
                          </span>
                        </div>

                        <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                          Создан: {user.created_at ? new Date(user.created_at).toLocaleString('ru-RU') : '—'}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hidden sm:block overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                      <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Email
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Имя пользователя
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Роль
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Telegram ID
                          </th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Дата создания
                          </th>
                          <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                            Действия
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                        {users.map((user) => (
                          <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                              {user.email}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.username || '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                  user.role === 'admin'
                                    ? 'bg-purple-100 text-purple-800 dark:bg-purple-800 dark:text-purple-100'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                                }`}
                              >
                                {user.role === 'admin' ? 'Администратор' : 'Пользователь'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.telegram_id ? user.telegram_id : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                              {user.created_at ? new Date(user.created_at).toLocaleString('ru-RU') : '-'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => handleEditUser(user)}
                                  className="text-primary-600 hover:text-primary-900 dark:text-primary-400 dark:hover:text-primary-300"
                                  title="Редактировать"
                                >
                                  <Edit2 className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    if (window.confirm(`Вы уверены, что хотите удалить пользователя "${user.email}"? Это действие нельзя отменить.`)) {
                                      deleteUserMutation.mutate(user.id);
                                    }
                                  }}
                                  className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                                  title="Удалить пользователя"
                                  disabled={deleteUserMutation.isPending}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <p className="text-center py-8 text-gray-500 dark:text-gray-400">
                  Пользователи не найдены
                </p>
              )}
            </Card>
          )}
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'usage' && <UserUsageTab />}

      {/* Model Modal */}
      <Modal
        isOpen={isModelModalOpen}
        onClose={resetModelForm}
        title={editingModel ? 'Редактировать модель' : 'Добавить модель'}
        footer={
          <>
            <Button variant="secondary" onClick={resetModelForm}>
              Отмена
            </Button>
            <Button
              onClick={handleModelSubmit}
              disabled={!modelName.trim() || !provider.trim() || !modelModelName.trim() || modelMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {editingModel ? 'Сохранить' : 'Добавить'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleModelSubmit} className="space-y-4">
          <Input
            label="Название модели"
            value={modelName}
            onChange={(e) => setModelName(e.target.value)}
            required
            placeholder="например: default, ocr, translation"
            disabled={!!editingModel}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Провайдер <span className="text-red-500">*</span>
            </label>
            <select
              value={provider}
              onChange={(e) => {
                const newProvider = e.target.value;
                setProvider(newProvider);
                // Auto-fill model name based on provider
                if (newProvider === 'openai' && !modelModelName) {
                  setModelModelName('gpt-5.2');
                } else if (newProvider === 'gemini' && !modelModelName) {
                  setModelModelName('gemini-2.5-flash');
                } else if (newProvider === 'anthropic' && !modelModelName) {
                  setModelModelName('claude-sonnet-4-6');
                }
                // Auto-fill base URL if empty
                if (!baseUrl) {
                  if (newProvider === 'openai') {
                    setBaseUrl('https://api.openai.com/v1');
                  } else if (newProvider === 'gemini') {
                    setBaseUrl('https://generativelanguage.googleapis.com/v1beta');
                  } else if (newProvider === 'anthropic') {
                    setBaseUrl('https://api.anthropic.com/v1');
                  }
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            >
              <option value="openai">OpenAI</option>
              <option value="gemini">Gemini (Google)</option>
              <option value="anthropic">Anthropic</option>
              <option value="local">Local</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Название модели провайдера <span className="text-red-500">*</span>
            </label>
            {provider === 'openai' ? (
              <>
                <select
                  value={modelModelName}
                  onChange={(e) => setModelModelName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                >
                  <option value="">Выберите модель...</option>
                  <optgroup label="GPT-5.3 Codex (новейшая, февраль 2026)">
                    <option value="gpt-5.3-codex">gpt-5.3-codex (агентная, для кода, самая мощная)</option>
                  </optgroup>
                  <optgroup label="GPT-5.2 серия (флагманские)">
                    <option value="gpt-5.2">gpt-5.2 (флагманская, рекомендуется)</option>
                    <option value="gpt-5.2-pro">gpt-5.2-pro (продвинутая, для сложных задач)</option>
                    <option value="gpt-5.2-instant">gpt-5.2-instant (быстрая, для повседневных задач)</option>
                  </optgroup>
                  <optgroup label="o-серия (reasoning, рассуждения)">
                    <option value="o3-pro">o3-pro (самый надёжный, максимальное время рассуждения)</option>
                    <option value="o3">o3 (мощные рассуждения, мат/наука/код)</option>
                    <option value="o3-mini">o3-mini (экономичный reasoning, мат/наука/код)</option>
                    <option value="o4-mini">o4-mini (быстрый reasoning, оптимизирован по стоимости)</option>
                  </optgroup>
                  <optgroup label="GPT-5 серия">
                    <option value="gpt-5">gpt-5 (базовая GPT-5)</option>
                    <option value="gpt-5.1">gpt-5.1 (улучшенная GPT-5)</option>
                    <option value="gpt-5-mini">gpt-5-mini (сбалансированная скорость/качество)</option>
                    <option value="gpt-5-nano">gpt-5-nano (самая быстрая и лёгкая)</option>
                  </optgroup>
                  <optgroup label="GPT-4.1 серия">
                    <option value="gpt-4.1">gpt-4.1 (улучшенная GPT-4)</option>
                    <option value="gpt-4.1-mini">gpt-4.1-mini (мини версия)</option>
                    <option value="gpt-4.1-nano">gpt-4.1-nano (нано версия)</option>
                  </optgroup>
                  <optgroup label="GPT-4o серия (legacy)">
                    <option value="gpt-4o">gpt-4o (мультимодальная)</option>
                    <option value="gpt-4o-mini">gpt-4o-mini (быстрая и дешёвая)</option>
                  </optgroup>
                </select>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <strong>Примечание:</strong> Модели gpt-oss-* (gpt-oss-120b, gpt-oss-20b) доступны через сторонние сервисы (AWS, HuggingFace) и могут требовать другой base_url. 
                  Добавьте их вручную, указав соответствующий базовый URL.
                </p>
              </>
            ) : provider === 'gemini' ? (
              <select
                value={modelModelName}
                onChange={(e) => setModelModelName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              >
                <option value="">Выберите модель...</option>
                <optgroup label="Серия Gemini 3.1 (новейшая, февраль 2026)">
                  <option value="gemini-3.1-pro-preview">gemini-3.1-pro-preview (самая мощная, 1M контекст, рекомендуется)</option>
                  <option value="gemini-3.1-flash-image-preview">gemini-3.1-flash-image-preview (генерация изображений)</option>
                </optgroup>
                <optgroup label="Серия Gemini 3">
                  <option value="gemini-3-flash-preview">gemini-3-flash-preview (Pro-уровень по цене Flash)</option>
                  <option value="gemini-3-pro-image-preview">gemini-3-pro-image-preview (генерация изображений)</option>
                </optgroup>
                <optgroup label="Серия Gemini 2.5 (стабильная, GA)">
                  <option value="gemini-2.5-pro">gemini-2.5-pro (продвинутые рассуждения, GA)</option>
                  <option value="gemini-2.5-flash">gemini-2.5-flash (быстрая и экономичная, GA)</option>
                  <option value="gemini-2.5-flash-lite">gemini-2.5-flash-lite (самая быстрая и дешёвая)</option>
                </optgroup>
                <optgroup label="Эмбеддинги">
                  <option value="gemini-embedding-001">gemini-embedding-001 (для создания эмбеддингов)</option>
                </optgroup>
              </select>
            ) : provider === 'anthropic' ? (
              <select
                value={modelModelName}
                onChange={(e) => setModelModelName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                required
              >
                <option value="">Выберите модель...</option>
                <optgroup label="Claude 4.6 (новейшая, февраль 2026)">
                  <option value="claude-opus-4-6">claude-opus-4-6 (самая мощная, агенты и код, $5/$25)</option>
                  <option value="claude-sonnet-4-6">claude-sonnet-4-6 (баланс скорости и интеллекта, $3/$15, рекомендуется)</option>
                </optgroup>
                <optgroup label="Claude 4.5">
                  <option value="claude-haiku-4-5">claude-haiku-4-5 (самая быстрая, $1/$5)</option>
                  <option value="claude-sonnet-4-5">claude-sonnet-4-5 (предыдущий Sonnet)</option>
                  <option value="claude-opus-4-5">claude-opus-4-5 (предыдущий Opus)</option>
                </optgroup>
                <optgroup label="Claude 4 (legacy)">
                  <option value="claude-sonnet-4-0">claude-sonnet-4-0</option>
                  <option value="claude-opus-4-0">claude-opus-4-0</option>
                </optgroup>
              </select>
            ) : (
              <Input
                value={modelModelName}
                onChange={(e) => setModelModelName(e.target.value)}
                required
                placeholder="например: model-name"
              />
            )}
          </div>
          <Input
            label="Base URL"
            value={baseUrl}
            onChange={(e) => setBaseUrl(e.target.value)}
            placeholder={provider === 'gemini' ? 'https://generativelanguage.googleapis.com/v1beta' : provider === 'openai' ? 'https://api.openai.com/v1' : provider === 'anthropic' ? 'https://api.anthropic.com/v1/' : 'Оставьте пустым для значения по умолчанию'}
          />
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
            <p className="text-sm text-blue-800 dark:text-blue-200">
              <strong>Примечание:</strong> Для эмбеддингов автоматически используется OpenAI text-embedding-3-small.
              <br />
              API ключ можно настроить двумя способами:
              <br />
              1. Глобально для всех моделей провайдера в разделе "Настройки" → "API ключи провайдеров"
              <br />
              2. Для конкретной модели через кнопку <Key className="h-3 w-3 inline" /> в списке моделей (приоритет выше)
              {provider === 'gemini' && (
                <>
                  <br />
                  <br />
                  <strong>Для Gemini:</strong> Используется OpenAI-совместимый endpoint по умолчанию.
                  Для стандартного REST API используйте: <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded">https://generativelanguage.googleapis.com/v1beta</code>
                </>
              )}
              {provider === 'anthropic' && (
                <>
                  <br />
                  <br />
                  <strong>Для Anthropic:</strong> Используется OpenAI-совместимый endpoint Anthropic.
                  Base URL по умолчанию: <code className="px-1 py-0.5 bg-blue-100 dark:bg-blue-800 rounded">https://api.anthropic.com/v1/</code>
                </>
              )}
            </p>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="isActive"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="isActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              Активна
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              id="deprecated"
              checked={deprecated}
              onChange={(e) => setDeprecated(e.target.checked)}
              className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded"
            />
            <label htmlFor="deprecated" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              Устарела <span className="text-xs text-gray-500 dark:text-gray-400">(модель снята с поддержки / заменена)</span>
            </label>
          </div>
        </form>
      </Modal>

      {/* API Key Modal */}
      <Modal
        isOpen={isApiKeyModalOpen}
        onClose={() => {
          setIsApiKeyModalOpen(false);
          setApiKey('');
          setSelectedModelName('');
        }}
        title="Обновить API ключ модели"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsApiKeyModalOpen(false);
                setApiKey('');
                setSelectedModelName('');
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={handleApiKeySubmit}
              disabled={!apiKey.trim() || apiKeyMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              Сохранить
            </Button>
          </>
        }
      >
        <form onSubmit={handleApiKeySubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Модель: {selectedModelName}
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Этот ключ будет использоваться только для данной модели. Если не указан, будет использован глобальный ключ провайдера.
            </p>
          </div>
          <Input
            label="API ключ"
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            required
            placeholder="Введите API ключ"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Ключ будет сохранён в базе данных. В продакшене рекомендуется использовать шифрование.
          </p>
        </form>
      </Modal>

      {/* Provider API Key Modal */}
      <Modal
        isOpen={isProviderApiKeyModalOpen}
        onClose={() => {
          setIsProviderApiKeyModalOpen(false);
          setProviderApiKey('');
          setSelectedProvider(null);
        }}
        title={`Настроить API ключ для ${selectedProvider === 'openai' ? 'OpenAI' : selectedProvider === 'gemini' ? 'Google Gemini' : 'Anthropic (Claude)'}`}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => {
                setIsProviderApiKeyModalOpen(false);
                setProviderApiKey('');
                setSelectedProvider(null);
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={(e) => {
                e.preventDefault();
                if (!providerApiKey.trim() || !selectedProvider) return;
                providerApiKeyMutation.mutate({ provider: selectedProvider, key: providerApiKey });
              }}
              disabled={!providerApiKey.trim() || !selectedProvider || providerApiKeyMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              Сохранить
            </Button>
          </>
        }
      >
        <form onSubmit={(e) => {
          e.preventDefault();
          if (!providerApiKey.trim() || !selectedProvider) return;
          providerApiKeyMutation.mutate({ provider: selectedProvider, key: providerApiKey });
        }} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Провайдер: {selectedProvider === 'openai' ? 'OpenAI' : selectedProvider === 'gemini' ? 'Google Gemini' : 'Anthropic (Claude)'}
            </label>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Этот ключ будет использоваться для всех моделей данного провайдера, если у модели не указан собственный ключ.
            </p>
          </div>
          <Input
            label="API ключ"
            type="password"
            value={providerApiKey}
            onChange={(e) => setProviderApiKey(e.target.value)}
            required
            placeholder={`Введите API ключ для ${selectedProvider === 'openai' ? 'OpenAI' : selectedProvider === 'gemini' ? 'Google Gemini' : 'Anthropic (Claude)'}`}
          />
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Ключ будет сохранён в базе данных. В продакшене рекомендуется использовать шифрование.
          </p>
        </form>
      </Modal>

      {/* Prompt Modal */}
      <Modal
        isOpen={isPromptModalOpen}
        onClose={resetPromptForm}
        title={editingPrompt ? 'Редактировать промпт' : 'Добавить промпт'}
        footer={
          <>
            <Button variant="secondary" onClick={resetPromptForm}>
              Отмена
            </Button>
            <Button
              onClick={handlePromptSubmit}
              disabled={!promptName.trim() || !taskType.trim() || !promptContent.trim() || promptMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {editingPrompt ? 'Сохранить' : 'Добавить'}
            </Button>
          </>
        }
      >
        <form onSubmit={handlePromptSubmit} className="space-y-4">
          <Input
            label="Название"
            value={promptName}
            onChange={(e) => setPromptName(e.target.value)}
            required
            placeholder="например: ocr, translate-with-glossary"
            disabled={!!editingPrompt}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Тип задачи
            </label>
            <select
              value={taskType}
              onChange={(e) => setTaskType(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            >
              <option value="ocr">OCR</option>
              <option value="translation">Перевод</option>
              <option value="glossary-extraction">Извлечение глоссария</option>
            </select>
          </div>
          <Textarea
            label="Содержание промпта"
            value={promptContent}
            onChange={(e) => setPromptContent(e.target.value)}
            required
            placeholder="Введите промпт..."
            rows={15}
          />
          <div className="flex items-center">
            <input
              type="checkbox"
              id="promptIsActive"
              checked={promptIsActive}
              onChange={(e) => setPromptIsActive(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
            />
            <label htmlFor="promptIsActive" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
              Активен
            </label>
          </div>
        </form>
      </Modal>

      {/* User Modal */}
      <Modal
        isOpen={isUserModalOpen}
        onClose={resetUserForm}
        title={editingUser ? 'Редактировать пользователя' : 'Добавить пользователя'}
        footer={
          <>
            <Button variant="secondary" onClick={resetUserForm}>
              Отмена
            </Button>
            <Button
              onClick={handleUserSubmit}
              disabled={!userEmail.trim() || (!editingUser && !userPassword.trim()) || userMutation.isPending}
            >
              <Save className="h-4 w-4 mr-2" />
              {editingUser ? 'Сохранить' : 'Добавить'}
            </Button>
          </>
        }
      >
        <form onSubmit={handleUserSubmit} className="space-y-4">
          <Input
            label="Email"
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            required
            placeholder="user@example.com"
          />
          <Input
            label="Имя пользователя"
            type="text"
            value={userUsername}
            onChange={(e) => setUserUsername(e.target.value)}
            placeholder="Опционально"
          />
          <Input
            label={editingUser ? 'Новый пароль (оставьте пустым, чтобы не менять)' : 'Пароль'}
            type="password"
            value={userPassword}
            onChange={(e) => setUserPassword(e.target.value)}
            required={!editingUser}
            placeholder={editingUser ? 'Оставьте пустым, чтобы не менять' : 'Минимум 6 символов'}
            minLength={6}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Роль <span className="text-red-500">*</span>
            </label>
            <select
              value={userRole}
              onChange={(e) => setUserRole(e.target.value as 'admin' | 'user')}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              required
            >
              <option value="user">Пользователь</option>
              <option value="admin">Администратор</option>
            </select>
          </div>
        </form>
      </Modal>
    </div>
  );
}
